import React, { useEffect, useState } from 'react';
import { PlusIcon, TrashIcon, PencilIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { config } from '../config/environment';

interface PaymentMethod {
  id: string;
  name: string;
  is_active: boolean;
  sort_order: number;
}

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
}

const BASE = `${config.apiBaseUrl}/coaches/me/payment-methods`;

async function apiFetch(path: string, opts?: RequestInit) {
  const r = await fetch(BASE + path, { headers: authHeaders(), ...opts });
  if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.detail || 'Request failed'); }
  return r.json();
}

const PaymentMethodsCard: React.FC = () => {
  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newName, setNewName] = useState('');
  const [addError, setAddError] = useState('');
  const [addSaving, setAddSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editError, setEditError] = useState('');

  useEffect(() => {
    apiFetch('')
      .then(setMethods)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const addMethod = async () => {
    const name = newName.trim();
    if (!name) { setAddError('Enter a method name.'); return; }
    setAddSaving(true);
    setAddError('');
    try {
      const created = await apiFetch('', { method: 'POST', body: JSON.stringify({ name }) });
      setMethods(prev => [...prev, created]);
      setNewName('');
    } catch (e: any) {
      setAddError(e.message);
    } finally {
      setAddSaving(false);
    }
  };

  const toggleActive = async (m: PaymentMethod) => {
    try {
      await apiFetch(`/${m.id}`, { method: 'PATCH', body: JSON.stringify({ is_active: !m.is_active }) });
      setMethods(prev => prev.map(x => x.id === m.id ? { ...x, is_active: !x.is_active } : x));
    } catch { /* silent */ }
  };

  const startEdit = (m: PaymentMethod) => {
    setEditingId(m.id);
    setEditName(m.name);
    setEditError('');
  };

  const saveEdit = async (id: string) => {
    const name = editName.trim();
    if (!name) { setEditError('Name cannot be empty.'); return; }
    try {
      await apiFetch(`/${id}`, { method: 'PATCH', body: JSON.stringify({ name }) });
      setMethods(prev => prev.map(x => x.id === id ? { ...x, name } : x));
      setEditingId(null);
    } catch (e: any) {
      setEditError(e.message);
    }
  };

  const deleteMethod = async (id: string) => {
    try {
      await apiFetch(`/${id}`, { method: 'DELETE' });
      setMethods(prev => prev.filter(x => x.id !== id));
    } catch { /* silent */ }
  };

  if (loading) return <div className="text-xs text-dark-muted py-2">Loading…</div>;
  if (error) return <div className="text-xs text-red-400 py-2">{error}</div>;

  return (
    <div className="bg-dark-card border border-dark-border rounded-xl p-5 space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-white">Payment Methods</h3>
        <p className="text-xs text-dark-muted mt-0.5">Methods available when recording a manual payment on a session.</p>
      </div>

      <ul className="space-y-1.5">
        {methods.map(m => (
          <li key={m.id} className="flex items-center gap-2">
            {editingId === m.id ? (
              <>
                <input
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && saveEdit(m.id)}
                  autoFocus
                  className="flex-1 text-sm border border-dark-border rounded px-2 py-1 bg-dark-canvas text-gray-200 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
                {editError && <span className="text-xs text-red-400">{editError}</span>}
                <button onClick={() => saveEdit(m.id)} className="p-1 text-green-400 hover:text-green-300">
                  <CheckIcon className="w-4 h-4" />
                </button>
                <button onClick={() => setEditingId(null)} className="p-1 text-gray-500 hover:text-gray-300">
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => toggleActive(m)}
                  className={`w-4 h-4 rounded flex-shrink-0 border transition-colors ${
                    m.is_active ? 'bg-primary-600 border-primary-600' : 'bg-transparent border-dark-border'
                  }`}
                  title={m.is_active ? 'Active — click to deactivate' : 'Inactive — click to activate'}
                >
                  {m.is_active && <CheckIcon className="w-3 h-3 text-white" />}
                </button>
                <span className={`flex-1 text-sm ${m.is_active ? 'text-gray-200' : 'text-gray-500 line-through'}`}>
                  {m.name}
                </span>
                <button onClick={() => startEdit(m)} className="p-1 text-gray-500 hover:text-gray-300 transition-colors">
                  <PencilIcon className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => deleteMethod(m.id)} className="p-1 text-gray-500 hover:text-red-400 transition-colors">
                  <TrashIcon className="w-3.5 h-3.5" />
                </button>
              </>
            )}
          </li>
        ))}
      </ul>

      <div className="flex gap-2 pt-1">
        <input
          value={newName}
          onChange={e => setNewName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && addMethod()}
          placeholder="Add method (e.g. Zelle)"
          className="flex-1 text-sm border border-dark-border rounded px-3 py-1.5 bg-dark-canvas text-gray-200 placeholder-dark-muted focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
        <button
          onClick={addMethod}
          disabled={addSaving}
          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          Add
        </button>
      </div>
      {addError && <p className="text-xs text-red-400">{addError}</p>}
    </div>
  );
};

export default PaymentMethodsCard;
